import { Box, Chip, Button, IconButton, Tooltip } from '@mui/material';
import { DebouncedTextField } from './DebouncedTextField';
import { CcfTypography, CcfBotIcon } from '@nice-ccf/ui-controls';
import { BotMessage } from './types';
import { useRef } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface Props {
  messages: BotMessage[];
  onChange: (messages: BotMessage[]) => void;
  disabled?: boolean;
}

const senderColor: Record<string, string> = {
  bot: '#e3f0fb',
  customer: '#e8f5e9',
};

const senderBorderColor: Record<string, string> = {
  bot: '#90caf9',
  customer: '#a5d6a7',
};

/**@example */
export function CopilotBotTranscriptSection({
  messages,
  onChange,
  disabled = false,
}: Props) {
  // Stable keys — maintained alongside every mutation so React moves component
  // instances with their rows on reorder/removal instead of reusing instances
  // by position, which would leave DebouncedTextField state on the wrong row.
  const keyCounter = useRef(0);
  const stableKeys = useRef<string[]>([]);

  if (stableKeys.current.length < messages.length) {
    while (stableKeys.current.length < messages.length) {
      stableKeys.current.push(`bot-msg-${keyCounter.current++}`);
    }
  } else if (stableKeys.current.length > messages.length) {
    stableKeys.current.length = messages.length;
  }

  /**@example */
  const updateMessage = (
    index: number,
    field: keyof BotMessage,
    value: string
  ) => {
    onChange(
      messages.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  };

  /**
   * Add a new empty message from the given sender at the end.
   * @example addMessage('customer')
   */
  const addMessage = (messageFrom: BotMessage['messageFrom']) => {
    stableKeys.current.push(`bot-msg-${keyCounter.current++}`);
    onChange([...messages, { messageFrom, message: '' }]);
  };

  /** Remove the message at the given index. @example removeMessage(0) */
  const removeMessage = (index: number) => {
    stableKeys.current.splice(index, 1);
    onChange(messages.filter((_, i) => i !== index));
  };

  /** Toggle a message's sender between bot and customer. @example toggleSender(0) */
  const toggleSender = (index: number) =>
    onChange(
      messages.map((m, i) =>
        i === index
          ? { ...m, messageFrom: m.messageFrom === 'bot' ? 'customer' : 'bot' }
          : m
      )
    );

  /**
   * Move the message at `index` one slot up or down, swapping with its neighbour.
   * @example moveMessage(1, -1)
   */
  const moveMessage = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= messages.length) return;
    [stableKeys.current[index], stableKeys.current[target]] = [
      stableKeys.current[target],
      stableKeys.current[index],
    ];
    const next = [...messages];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {messages.map((msg, index) => (
        <Box
          key={stableKeys.current[index]}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            borderLeft: `2px solid ${
              senderBorderColor[msg.messageFrom] ?? '#ccc'
            }`,
            p: 1.5,
            backgroundColor: senderColor[msg.messageFrom] ?? '#fafafa',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Tooltip title="Click to switch sender">
              <Chip
                onClick={() => toggleSender(index)}
                icon={
                  msg.messageFrom === 'bot' ? (
                    <CcfBotIcon sx={{ fontSize: 14 }} fillColor="#fff" />
                  ) : (
                    <PersonIcon style={{ fontSize: 14, color: '#fff' }} />
                  )
                }
                label={msg.messageFrom}
                size="small"
                sx={{
                  cursor: 'pointer',
                  backgroundColor: senderBorderColor[msg.messageFrom] ?? '#ccc',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: 'capitalize',
                  '& .MuiChip-icon': { color: '#fff', margin: '0px' },
                  '& .MuiChip-label': { padding: '0px' },
                  padding: '10px',
                }}
              />
            </Tooltip>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Move up">
                <span>
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={() => moveMessage(index, -1)}
                  >
                    <KeyboardArrowUpIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    size="small"
                    disabled={index === messages.length - 1}
                    onClick={() => moveMessage(index, 1)}
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove message">
                <IconButton
                  size="small"
                  onClick={() => removeMessage(index)}
                  sx={{ color: '#d32f2f' }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <DebouncedTextField
            fullWidth
            size="small"
            multiline
            minRows={1}
            label="Message"
            value={msg.message}
            onChange={(v) => updateMessage(index, 'message', v)}
          />
        </Box>
      ))}

      {messages.length === 0 && (
        <CcfTypography
          variant="body2"
          sx={{ color: '#9e9e9e', textAlign: 'center', py: 2 }}
        >
          No messages.
        </CcfTypography>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => addMessage('bot')}
          sx={{ textTransform: 'none' }}
        >
          Add Bot Message
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => addMessage('customer')}
          sx={{ textTransform: 'none' }}
        >
          Add Customer Message
        </Button>
      </Box>
    </Box>
  );
}

export default CopilotBotTranscriptSection;
